import { runJob, IJobResult } from '../../../../../../FileRenderer/runJob';
import { parseFasta, ISequenceRecord } from '../validation';

export interface IFetchTableRow {
    id: string;
    sequence: string;
}

export interface IFetchResult {
    updatedRows: Record<number, Partial<IFetchTableRow>>;
    errorMessage?: string;
    detailedOutput: string;
}

export async function fetchSequencesByIds(
    sequenceRetrievalAppUri: string,
    rows: Record<number, IFetchTableRow>,
): Promise<IFetchResult> {
    // Only fetch for IDs that don't already have a sequence
    const idsToFetch = Object.values(rows)
        .filter((row) => row.id.trim() && !row.sequence?.trim())
        .map((row) => row.id.trim());

    if (idsToFetch.length === 0) {
        return { updatedRows: {}, detailedOutput: '' };
    }

    const idsFileContent = idsToFetch.join('\n');
    const idsFileData = new TextEncoder().encode(idsFileContent);

    const payload = {
        appUri: sequenceRetrievalAppUri,
        arguments: ['--input', 'ids.txt'],
        files: {
            '/ids.txt': { data: idsFileData },
        },
    };

    const result = (await runJob(payload, () => {})) as IJobResult;

    const fastaFile = result.files['/sequences.fasta'];
    const decoder = new TextDecoder();

    // Helper function to create detailed error info
    const createDetailedOutput = () => {
        const stdout = decoder.decode(result.stdout);
        const stderr = decoder.decode(result.stderr);
        return `Exit code: ${result.exit_code}\n\nLogs:\n${stdout}\n\n${stderr}`;
    };
    let detailedOutput = createDetailedOutput();

    // If no FASTA file exists, handle as complete failure
    if (!fastaFile) {
        // Mark all rows with IDs as failed
        const updatedRows: Record<number, Partial<IFetchTableRow>> = {};
        return {
            updatedRows,
            errorMessage: 'Sequence retrieval failed',
            detailedOutput: detailedOutput,
        };
    }

    const fastaText = decoder.decode(fastaFile.data);
    let sequenceRecords: ISequenceRecord[] = [];
    try {
        sequenceRecords = parseFasta(fastaText, { allow_empty_sequence: true });
    } catch (error) {
        sequenceRecords = [];
    }
    const sequenceMap = new Map<string, string>();
    sequenceRecords.forEach((seq) => {
        sequenceMap.set(seq.sequenceId, seq.sequence);
    });
    const updatedRows: Record<number, Partial<IFetchTableRow>> = {};
    const notFoundIds: string[] = [];

    Object.entries(rows).forEach(([rowIndex, row]) => {
        const rowId = row.id.trim();
        // Only process rows that were in the fetch list (no existing sequence)
        if (rowId && !row.sequence?.trim()) {
            const sequence = sequenceMap.get(rowId);
            if (sequence) {
                updatedRows[Number(rowIndex)] = { sequence };
            } else {
                notFoundIds.push(rowId);
            }
        }
    });
    let errorMessage: string | undefined = result.exit_code === 0 ? undefined : 'Sequence retrieval failed';
    if (notFoundIds.length > 0) {
        errorMessage =
            (errorMessage ? errorMessage + '\n' : '') +
            `Sequences not found for the following IDs: ${notFoundIds.join(', ')}`;
    }
    return {
        updatedRows,
        errorMessage,
        detailedOutput: detailedOutput,
    };
}

export interface IClipboardParseResult {
    rowsToUpdate: Record<number, Partial<IFetchTableRow>>;
}

export function parseClipboardData(rawText: string, focusedRow: number): IClipboardParseResult {
    const rowsToUpdate: Record<number, Partial<IFetchTableRow>> = {};

    let isPastingFromSpreadsheet = true;
    const lines = rawText.split('\n');
    for (const line of lines) {
        if (!line.includes('\t')) {
            isPastingFromSpreadsheet = false;
            break;
        }
    }

    if (isPastingFromSpreadsheet) {
        for (let i = 0; i < lines.length; i++) {
            const columns = lines[i].trim().split('\t');
            const newRowIndex = focusedRow + i;
            const id = columns[0]?.trim() || '';
            rowsToUpdate[newRowIndex] = { id, sequence: '' };
        }
    } else if (rawText.includes('\n')) {
        const ids = rawText
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);
        for (let i = 0; i < ids.length; i++) {
            const newRowIndex = focusedRow + i;
            rowsToUpdate[newRowIndex] = { id: ids[i], sequence: '' };
        }
    } else {
        rowsToUpdate[focusedRow] = { id: rawText };
    }

    return { rowsToUpdate };
}

export function findNextAvailableRowIndex(rows: Record<number, Record<number, string>>): number {
    let nextRowIndex = 0;
    for (let i = Object.keys(rows).length - 1; i >= 0; i--) {
        const row = rows[i];
        if (row && (row[0]?.trim() || row[1]?.trim())) {
            nextRowIndex = i + 1;
            break;
        }
    }
    return nextRowIndex;
}

export function updateFetchModalRows(
    prevRows: Record<number, IFetchTableRow>,
    newRows: Record<number, Partial<IFetchTableRow>>,
): Record<number, IFetchTableRow> {
    const updatedRows = { ...prevRows };
    Object.entries(newRows).forEach(([rowIndex, rowData]) => {
        const index = Number(rowIndex);
        updatedRows[index] = { ...updatedRows[index], ...rowData };
    });

    // Add empty row if last row has content (same logic as SequenceInput)
    const rowIndices = Object.keys(updatedRows).map(Number);
    if (rowIndices.length > 0) {
        const maxRowIndex = Math.max(...rowIndices);
        const lastRow = updatedRows[maxRowIndex];
        if (lastRow?.id || lastRow?.sequence) {
            // Last row not empty so add new empty row
            updatedRows[maxRowIndex + 1] = { id: '', sequence: '' };
        }
    }

    return updatedRows;
}

export function convertRowsToSaveFormat(rows: Record<number, IFetchTableRow>): Record<number, Record<number, string>> {
    const sequencesToSave: Record<number, Record<number, string>> = {};

    Object.entries(rows).forEach(([rowIndex, row]) => {
        if (row.id.trim() && row.sequence?.trim()) {
            sequencesToSave[Number(rowIndex)] = {
                0: row.id.trim(),
                1: row.sequence.trim(),
            };
        }
    });

    return sequencesToSave;
}

export function calculateFetchModalTableDimensions(
    rows: Record<number, IFetchTableRow>,
    containerWidth: number | null,
    scrollbarWidth: number,
): { columnWidths: number[]; rowCount: number } {
    const columnWidths = [200, (containerWidth ?? 600) - 200 - scrollbarWidth];

    const rowIndices = Object.keys(rows).map(Number);
    const maxRowIndex = rowIndices.length > 0 ? Math.max(...rowIndices) : -1;
    const rowCount = Math.max(100, maxRowIndex + 1);

    return { columnWidths, rowCount };
}

export function extractRowsWithIdsOnly(
    sequenceRows: Record<number, Record<number, string>>,
): Record<number, IFetchTableRow> {
    const filteredRows: Record<number, IFetchTableRow> = {};

    Object.entries(sequenceRows).forEach(([rowIndex, row]) => {
        const id = row[0]?.trim();
        const sequence = row[1]?.trim();

        if (id && !sequence) {
            filteredRows[Number(rowIndex)] = {
                id: id,
                sequence: '',
            };
        }
    });

    return filteredRows;
}
